import {
  Box,
  List,
  ListItem,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import { Send, Close } from "@mui/icons-material";
import { locale as dayjsLocale } from "dayjs";

/* =========================
   TYPES
========================= */

export type ChatMessage = {
  id: string | number;
  message: string;
  author: string;
  created: string;
};

interface ChatPanelProps {
  messages: ChatMessage[];
  emptyText: string;
  inputValue: string;
  onInputChange: (_value: string) => void;
  onSend: () => void;
  sendDisabled?: boolean;
  placeholder?: string;
  onDelete?: (_id: string | number) => void;
}

/* =========================
   DATE HELPERS
========================= */

const DATE_REGEX = /^(\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2})(?::(\d{2}))?$/;

const parseDateSafely = (value: string): Date | null => {
  const native = new Date(value);
  if (!Number.isNaN(native.getTime())) {
    return native;
  }

  const match = DATE_REGEX.exec(value);
  if (!match) return null;

  const [, dd, mm, yyyy, hh, min, ss] = match;

  return new Date(
    Number(yyyy),
    Number(mm) - 1,
    Number(dd),
    Number(hh),
    Number(min),
    Number(ss ?? 0),
  );
};

const formatTime = (value: string): string => {
  const d = parseDateSafely(value);
  if (!d) return "—";

  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();

  return sameDay
    ? d.toLocaleTimeString(dayjsLocale(), {
        hour: "2-digit",
        minute: "2-digit",
      })
    : `${d.toLocaleDateString(dayjsLocale(), {
        day: "2-digit",
        month: "2-digit",
      })} ${d.toLocaleTimeString(dayjsLocale(), {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
};

/* =========================
   COMPONENT
========================= */

export const ChatPanel = ({
  messages,
  emptyText,
  inputValue,
  onInputChange,
  onSend,
  sendDisabled,
  placeholder,
  onDelete,
}: ChatPanelProps) => {
  const hasMessages = messages.length > 0;

  return (
    <>
      <Box sx={{ flex: 1, overflowY: "auto", maxHeight: 200 }}>
        <List dense disablePadding>
          {hasMessages &&
            messages.map((m) => (
              <ListItem key={m.id} sx={{ px: 0.5, py: 0.25 }}>
                <Stack
                  sx={{
                    flexGrow: 1,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    position: "relative",
                    maxWidth: "100%",
                  }}
                >
                  {onDelete && (
                    <IconButton
                      size="small"
                      onClick={() => onDelete(m.id)}
                      sx={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        opacity: 0.6,
                        "&:hover": { opacity: 1 },
                      }}
                    >
                      <Close sx={{ fontSize: "0.8rem" }} />
                    </IconButton>
                  )}

                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 500,
                      color: "text.secondary",
                    }}
                  >
                    {m.author}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      hyphens: "auto",
                    }}
                  >
                    {m.message}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "0.6rem",
                      color: "text.disabled",
                      alignSelf: "flex-end",
                    }}
                  >
                    {formatTime(m.created)}
                  </Typography>
                </Stack>
              </ListItem>
            ))}

          {!hasMessages && (
            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              sx={{ width: "100%", mt: 1 }}
            >
              {emptyText}
            </Typography>
          )}
        </List>
      </Box>

      <Stack direction="row" spacing={1} mt={1}>
        <TextField
          size="small"
          multiline
          maxRows={2}
          fullWidth
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
        />
        <Button
          size="small"
          variant="contained"
          onClick={onSend}
          disabled={sendDisabled || !inputValue.trim()}
        >
          <Send fontSize="small" />
        </Button>
      </Stack>
    </>
  );
};
